import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { logEventSafe } from '../../analytics/analytics';
import { PageBackLink, PageHero, PagePanel, PageShell } from '../MarketingPageShell';

const MAX_EXPERIENCE = 500;
const MAX_IMPROVEMENT = 500;
const MAX_EMAIL_LENGTH = 100;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FeedbackPage = () => {
  const location = useLocation();
  const auth = getAuth();
  const db = getFirestore();

  const [feedback, setFeedback] = useState({
    experience: '',
    improvement: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [notSignedIn, setNotSignedIn] = useState(false);

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Feedback'
    });
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setNotSignedIn(!user);
    });

    return () => unsubscribe();
  }, [auth]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'experience') {
      newValue = value.slice(0, MAX_EXPERIENCE);
    } else if (name === 'improvement') {
      newValue = value.slice(0, MAX_IMPROVEMENT);
    } else if (name === 'email') {
      newValue = value.slice(0, MAX_EMAIL_LENGTH);
    }

    setFeedback((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    const user = auth.currentUser;
    if (!user) {
      setError('You must be signed in to submit feedback.');
      return;
    }

    const exp = feedback.experience.trim();
    const imp = feedback.improvement.trim();
    const email = feedback.email.trim();

    if (!exp || !imp) {
      setError('Please fill out both "What went well?" and "What could be better?"');
      return;
    }
    if (email && !EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address or leave it blank.');
      return;
    }

    setIsSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const feedbackId = `${user.uid}_${today}`;
      const feedbackRef = doc(db, 'feedback', feedbackId);

      const existing = await getDoc(feedbackRef);
      if (existing.exists()) {
        setError('You have already submitted feedback today. Please try again tomorrow.');
        setIsSubmitting(false);
        return;
      }

      await setDoc(feedbackRef, {
        experience: exp,
        improvement: imp,
        email: email || null,
        userId: user.uid,
        createdAt: serverTimestamp(),
      });

      setInfoMessage('Thank you for your feedback!');
      setFeedback({ experience: '', improvement: '', email: '' });

      logEventSafe('submit_feedback', {
        page_path: location.pathname,
        included_email: Boolean(email),
      });
    } catch (err) {
      console.error('Error saving feedback', err);
      if (err.code === 'permission-denied') {
        setError('You can only submit one piece of feedback per day.');
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="space-y-10 py-4">
        <PageBackLink />
        <PagePanel className="mx-auto max-w-2xl">
          <div className="mb-8">
            <PageHero eyebrow="Feedback" title="Product Feedback" description="Help us improve Toolip." />
          </div>

          {error && (
            <div className="mb-4 rounded border border-red-400 bg-red-100 px-4 py-2 text-red-700">
              {error}
            </div>
          )}
          {infoMessage && (
            <div className="mb-4 rounded border border-green-400 bg-green-100 px-4 py-2 text-green-700">
              {infoMessage}
            </div>
          )}

          {notSignedIn && (
            <div className="mb-4 rounded border border-gray-600 bg-gray-700/50 px-4 py-3 text-sm text-gray-200">
              <div className="flex items-center justify-between gap-4">
                <span>You must be signed in to submit feedback.</span>
                <Link to="/LoginPage" className="whitespace-nowrap text-blue-400 underline hover:text-blue-300">
                  Sign in ->
                </Link>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="experience" className="mb-2 block text-sm font-medium text-gray-300 select-none">
                What went well?
              </label>
              <textarea
                id="experience"
                name="experience"
                value={feedback.experience}
                onChange={handleInputChange}
                rows={4}
                maxLength={MAX_EXPERIENCE}
                className="w-full rounded-lg border border-gray-700 bg-gray-700 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Share your positive experiences..."
                required
              />
              <p className="mt-1 text-xs text-gray-400">{feedback.experience.length}/{MAX_EXPERIENCE}</p>
            </div>

            <div>
              <label htmlFor="improvement" className="mb-2 block text-sm font-medium text-gray-300 select-none">
                What could be better?
              </label>
              <textarea
                id="improvement"
                name="improvement"
                value={feedback.improvement}
                onChange={handleInputChange}
                rows={4}
                maxLength={MAX_IMPROVEMENT}
                className="w-full rounded-lg border border-gray-700 bg-gray-700 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tell us where we can improve..."
                required
              />
              <p className="mt-1 text-xs text-gray-400">{feedback.improvement.length}/{MAX_IMPROVEMENT}</p>
            </div>

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-300 select-none">
                Your email (optional)
              </label>
              <input
                type="email"
                id="email"
                name="email"
                maxLength={MAX_EMAIL_LENGTH}
                value={feedback.email}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-700 bg-gray-700 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="help@example.com"
              />
              <p className="mt-1 text-xs text-gray-400">{feedback.email.length}/{MAX_EMAIL_LENGTH}</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || notSignedIn}
              className="w-full rounded-lg bg-blue-600 py-3 text-white transition-colors duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {notSignedIn ? 'Sign in to submit feedback' : isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        </PagePanel>
      </div>
    </PageShell>
  );
};

export default FeedbackPage;
